import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  FlatList,
  TouchableOpacity,
} from "react-native";

import { useHistory, useLocation, useParams } from "react-router-dom";
import SimpleButton from "../components/SimpleButton";
import InputField from "../components/InputField";
import {
  AnalyzeMatch,
  ChangeGameTime,
  UpdateUserMatchMappings,
  ClearAnalysisError,
} from "../redux/actions/analysisAction";
import {
  FetchMatchStringFromServer,
  PushMatchStringToServer,
  SetFullAnalyseString,
} from "../redux/actions/controlAction";
import { FaArrowCircleLeft, FaPencilAlt } from "react-icons/fa";
import { connect, useDispatch, useSelector } from "react-redux";
import { getPlayerMatchMapping } from "../redux/actions/userMatchMappingAction";
import toast from "react-hot-toast";
import { EditNote, Home, Padding } from "@mui/icons-material";
import { updateEventLog, pushCompletedTechQCString, getMatchById } from "../config/mainRequest";
import { createRunAnalysisCheckpoint } from "../persistence/managers/CheckpointManager";
import { stripStrayWords } from "../persistence/guard/stringGuard";
import { trackGA } from "../utils/ga";
import { ActivityIndicator } from "react-native-web";
import {
  AppBar,
  Box,
  Dialog,
  Modal,
  TextField,
  Toolbar,
  Typography,
  Button,
  Stack,
  IconButton,
} from "@mui/material";

const position_list = [
  { id: 1, name: "GOALKEEPER", alias: "GK" }, //0
  { id: 2, name: "CENTER BACK", alias: "CB" }, //1
  { id: 3, name: "LEFT SIDE BACK", alias: "LB" }, //2
  { id: 4, name: "RIGHT SIDE BACK", alias: "RB" }, //3
  { id: 5, name: "LEFT WING BACK", alias: "LWB" }, //4
  { id: 6, name: "RIGHT WING BACK", alias: "RWB" }, //5
  { id: 7, name: "DEFENSIVE MIDFIELDER", alias: "CDM" }, //6
  { id: 8, name: "CENTRAL MIDFIELDER", alias: "CM" }, //7
  { id: 9, name: "ATTACKING MIDFIELDER", alias: "CAM" }, //8
  { id: 10, name: "LEFT MIDFIELDER", alias: "LM" }, //9
  { id: 11, name: "RIGHT MIDFIELDER", alias: "RM" }, //10
  { id: 12, name: "LEFT WINGER", alias: "LW" }, //11
  { id: 13, name: "RIGHT WINGER", alias: "RW" }, //12
  { id: 14, name: "LEFT FORWARD", alias: "LF" }, //13
  { id: 15, name: "RIGHT FORWARD", alias: "RF" }, //14
  { id: 16, name: "CENTER FORWARD", alias: "CF" }, //15
  { id: 17, name: "STRIKER", alias: "ST" }, //16
];

const MatchUpdate = (props) => {
  const { matchId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const { analystName, role } = useSelector((state) => ({
    analystName: state.auth.analystName,
    role: state.auth.accountRole,
  }));

  // Google Analytics helper — attaches shared context to every event.
  const track = useCallback(
    (action, extra = {}) =>
      trackGA(action, {
        screen: "match_update",
        match_id: matchId,
        user_id: props.analyst_id,
        role,
        ...extra,
      }),
    [matchId, props.analyst_id, role]
  );

  // Derives the event count + length from the match string so analytics can
  // record "what data was passed" without sending the full (large) string,
  // which GA4 would truncate at 100 chars per param.
  const describeString = (str) => {
    if (!str) return { string_event_count: 0, string_length: 0, string_tail: "" };
    return {
      string_event_count: str
        .split(",")
        .filter(
          (s) => s.trim() && s.trim() !== "X-X-X-X-X-X-X-X-X-X-X-X-X-X"
        ).length,
      string_length: str.length,
      // GA4 truncates params at 100 chars, so send only the last 100 chars.
      string_tail: str.slice(-100),
    };
  };

  const [userMappingRequest, setUserMappingRequest] = useState([]);
  const [showSaveStatus, setShowSaveStatus] = useState(false);
  const [showRunStatus, setShowRunStatus] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [matchDetails, setMatchDetails] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSaveStatus(false);
      // setShowRunStatus(false);
    }, 5000);

    return () => clearTimeout(timer); // Cleanup the timer
  }, [showSaveStatus, showRunStatus]);

  const [isDarkMode, setIsDarkMode] = useState(
    document.body.classList.contains("theme-dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("theme-dark"));
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const dispatch = useDispatch();

  // useEffect(() => {
  //   dispatch(
  //     FetchMatchStringFromServer(matchId, props.analyst_id, props.token)
  //   );
  // }, []);

  useEffect(() => {
    if (location.state) {
      dispatch(
        FetchMatchStringFromServer(matchId, props.analyst_id, props.token)
      );
    }
  }, []);

  const showHomeBtn = location?.state?.TechQCSubmit;

  useEffect(() => {
    if (!matchId || !props.token) return;
    let cancelled = false;
    getMatchById({ match_id: matchId, token: props.token })
      .then((details) => {
        if (!cancelled && details) setMatchDetails(details);
      })
      .catch((err) =>
        console.error("[MatchUpdate] match fetch failed:", err?.message)
      );
    return () => {
      cancelled = true;
    };
  }, [matchId, props.token]);

  const fetchAllPlayersData = async () => {
    if (matchId && props.all_matches) {
      let allMatches = props.all_matches;
      let matchData = allMatches.find((i) => {
        return i.id.toString() === matchId;
      });

      props.getPlayerMatchMapping(
        matchId,
        matchData.team_a_details.id,
        matchData.team_b_details.id,
        props.token
      );
    }
  };

  useEffect(() => {
    fetchAllPlayersData();
  }, [props.all_matches, matchId]);

  useEffect(() => {
    props.ClearAnalysisError();
  }, []);

  const onSaveStringPress = async () => {
    setShowEdit(false);
    const { string: cleanString, removed } = stripStrayWords(props.matchString?.[matchId]);
    if (removed.length > 0) {
      props.SetFullAnalyseString({ id: matchId, string: cleanString, source: "stray-text-removed" });
      toast(`Removed text that isn't an event: ${removed.join(" ")}`);
    }
    if (cleanString && cleanString !== "") {
      try {
        await props.PushMatchStringToServer(
          matchId,
          props.analyst_id,
          cleanString,
          props.token
        );

        toast("STRING SAVED");
      } catch (err) {
        toast("ERROR SAVING STRING TO SERVER");
      }
    }
  };

  useEffect(() => {
    if (showRunStatus) {
      if (props.analysis_result) {
        // GA – analysis returned a result (success).
        track("run_analysis_result", {
          status: "success",
          ...describeString(props.matchString?.[matchId]),
        });
        // Store the current time in localStorage and disable the button
        localStorage.setItem("buttonClickTime", Date.now().toString());
        setIsDisabled(true);
        setTimeLeft(600); // Set 10 minutes (600 seconds)

        // Countdown timer
        const interval = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setIsDisabled(false);
              localStorage.removeItem("buttonClickTime");
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }
  }, [props.analysis_result, showRunStatus]);

  useEffect(() => {
    if (showRunStatus && props.analysis_error) {
      // GA – analysis returned an error.
      track("run_analysis_result", {
        status: "error",
        error: String(props.analysis_error).slice(0, 100),
      });
    }
  }, [props.analysis_error, showRunStatus]);

  useEffect(() => {
    if (props.analysis_result) {
      //api call for update analysis
      updateEventLog({
        matchId: matchId,
        analystId: props.analyst_id,
        eventLog: "end",
        token: props.token,
      });
    }
  }, [props.analysis_result]);

  useEffect(() => {
    // Check for saved timestamp in localStorage on mount
    const savedTime = localStorage.getItem("buttonClickTime");

    if (savedTime) {
      const timeDifference = Date.now() - parseInt(savedTime, 10);
      const fifteenMinutes = 600000; // 10 minutes in milliseconds

      if (timeDifference >= fifteenMinutes) {
        // Enable the button if 15 minutes have passed
        setIsDisabled(false);
        localStorage.removeItem("buttonClickTime");
      } else {
        // Calculate remaining time and disable button
        setIsDisabled(true);
        const remainingTime = fifteenMinutes - timeDifference;
        setTimeLeft(Math.ceil(remainingTime / 1000)); // Set time left in seconds

        // Countdown timer
        const interval = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setIsDisabled(false);
              localStorage.removeItem("buttonClickTime");
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // Cleanup interval on component unmount
        return () => clearInterval(interval);
      }
    }
  }, []);

  // Re-runs a string the validation gate blocked; the backend only allows the accounts it
  // reported as can_override, and stores the reason with the override.
  const handleOverrideRunClick = () => {
    const reason = window.prompt(
      "This string failed validation. Why should it run anyway? (saved with the override)"
    );
    if (!reason || !reason.trim()) return;
    setShowRunStatus(true);
    track("run_analysis_override", { reason: reason.trim().slice(0, 100) });
    props.analyzeMatch(matchId, props.matchString?.[matchId], props.token, reason.trim());
  };

  const handleOnRunAnalysisClick = () => {
    setShowRunStatus(true);
    let aPPFlag = 0;
    let bPPFlag = 0;
    for (let i = 0; i < props.team_a_data.length; i++) {
      if (
        parseInt(props.team_a_data[i].preferred_position) < 1 ||
        props.team_a_data[i].preferred_position === undefined ||
        props.team_a_data[i].preferred_position === null
      ) {
        aPPFlag = 1;
      }
    } //=============== check

    for (let i = 0; i < props.team_b_data.length; i++) {
      if (
        parseInt(props.team_b_data[i].preferred_position) < 1 ||
        props.team_b_data[i]?.preferred_position === undefined ||
        props.team_b_data[i]?.preferred_position === null
      ) {
        bPPFlag = 1;
      }
    } //=============== check

    if (aPPFlag === 0 && bPPFlag === 0) {
      let allMatches = props.all_matches;
      let matchData = allMatches.find((i) => {
        return i.id.toString() === matchId;
      });

      // Force-push the latest match string to server before running analysis
      const matchString = props.matchString?.[matchId];

      // GA – run analysis clicked, logging the data being passed to analysis.
      track("run_analysis_clicked", describeString(matchString));

      if (matchString) {
        pushCompletedTechQCString(
          matchId,
          props.analyst_id,
          props.token,
          matchString,
          1 // force_update flag
        );

        // Snapshot the match string into version history, labelled 'run-analysis'.
        // Writes to IDB first, then immediately flushes to Firestore — no periodic
        // timer delay.  Fire-and-forget so it never blocks the analysis dispatch.
        createRunAnalysisCheckpoint({
          matchId,
          analystId: String(props.analyst_id),
          currentString: matchString,
        }).catch((e) =>
          console.warn("[MatchUpdate] run-analysis checkpoint failed:", e.message)
        );
      }

      props.analyzeMatch(matchId, matchString, props.token);
      props.getPlayerMatchMapping(
        matchId,
        matchData.team_a_details.id,
        matchData.team_b_details.id,
        props.token
      );
    } else {
      track("run_analysis_blocked", { reason: "missing_preferred_position" });
      toast.error("Please fill the preferred positions for all the players!");
    }
  };

  const formatTimeFromSeconds = (seconds) => {
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const changeUserMappingData = (
    team,
    match,
    user,
    gameTime,
    preferredPosition,
    startGameTime,
    endGameTime
  ) => {
    let allMatches = props.all_matches;
    let matchData = allMatches.find((i) => {
      return i.id.toString() === matchId;
    });
    const currentUserMapping = userMappingRequest;
    const mapping = currentUserMapping.find((m) => {
      return m.user_id === user && m.team_id === team && m.match_id === match;
    });

    if (mapping) {
      if (gameTime) {
        mapping["game_time"] = parseInt(gameTime);
      }
      if (preferredPosition) {
        mapping["preferred_position"] = preferredPosition;
      }
      if (startGameTime) {
        mapping["start_gametime"] = parseInt(startGameTime);
      }
      if (endGameTime) {
        mapping["end_gametime"] = parseInt(endGameTime);
      }
    } else {
      // Pull the full player record so the bulk payload carries every field
      // the create_user_match_mapping_bulk endpoint expects.
      const source =
        (props.team_a_data || []).find(
          (p) => p.user === user && p.team === team && p.match === match
        ) ||
        (props.team_b_data || []).find(
          (p) => p.user === user && p.team === team && p.match === match
        );

      const newMapping = {
        error: null,
        user_id: user,
        match_id: match,
        team_id: team,
        tournament_id:
          matchDetails?.tournament_id ??
          matchData?.tournament_id ??
          matchData?.tournament?.id ??
          null,
        player_name: source?.user_name ?? null,
        jersey_number: source?.jersey_number ?? null,
        preferred_position: source?.preferred_position ?? null,
        preferred_position_sub_level: source?.preferred_position_sub_level ?? null,
        lineup_notation: source?.lineup_notation ?? null,
        start_gametime: source?.start_gametime ?? null,
        end_gametime: source?.end_gametime ?? null,
        game_time: source?.game_time ?? null,
        substituted_player_id: source?.substituted_player_id ?? null,
        substituted_player_name: source?.substituted_player_name ?? null,
      };
      if (gameTime) {
        newMapping["game_time"] = parseInt(gameTime);
      }
      if (preferredPosition) {
        newMapping["preferred_position"] = preferredPosition;
      }
      if (startGameTime) {
        newMapping["start_gametime"] = parseInt(startGameTime);
      }
      if (endGameTime) {
        newMapping["end_gametime"] = parseInt(endGameTime);
      }
      currentUserMapping.push(newMapping);
    }
    setUserMappingRequest(currentUserMapping);
  };

  // Builds the full lineup payload (both teams) in the shape the bulk endpoint
  // expects, applying any edited values from userMappingRequest on top.
  const buildFullLineupData = () => {
    let allMatches = props.all_matches;
    let matchData = allMatches.find((i) => i.id.toString() === matchId);
    const players = [
      ...(props.team_a_data || []),
      ...(props.team_b_data || []),
    ];

    return players.map((p) => {
      const edit = userMappingRequest.find(
        (m) =>
          m.user_id === p.user &&
          m.team_id === p.team &&
          m.match_id === p.match
      );
      const base = {
        error: null,
        user_id: p.user,
        match_id: p.match,
        team_id: p.team,
        tournament_id:
          matchData?.tournament_details?.id ??
          matchData?.tournament_id ??
          matchData?.tournament?.id ??
          null,
        player_name: p.user_name ?? null,
        jersey_number: p.jersey_number ?? null,
        preferred_position: p.preferred_position ?? null,
        preferred_position_sub_level: p.preferred_position_sub_level ?? null,
        lineup_notation: p.lineup_notation ?? null,
        start_gametime: p.start_gametime ?? null,
        end_gametime: p.end_gametime ?? null,
        game_time: p.game_time ?? null,
        substituted_player_id: p.substituted_player_id ?? null,
        substituted_player_name: p.substituted_player_name ?? null,
      };
      return edit ? { ...base, ...edit } : base;
    });
  };

  const onResponse = (result) => {
    if (result.success) {
      fetchAllPlayersData();
    }
  };

  return (
    <View style={styles.container}>
      <View
        id="toolbar"
        style={{
          backgroundColor: "var(--surface-1)",
          width: "100%",
          paddingVertical: "1rem",
          flexDirection: "row",
          paddingHorizontal: "1rem",
          boxShadow: isDarkMode ? "0px 2px 6px rgba(0,0,0,0.5)" : "0px 2px 6px #00000020",
          alignItems: "center",
          borderBottom: isDarkMode ? "1px solid var(--border-1)" : "none",
        }}
      >
        <TouchableOpacity onPress={() => history.goBack()}>
          <FaArrowCircleLeft size={20} color={isDarkMode ? "#fff" : "#000"} />
        </TouchableOpacity>
        <Text
          style={{
            color: "#1976D2",
            fontSize: "12.5px",
            fontWeight: "600",
            marginStart: "1rem",
          }}
        >
          {matchId}
        </Text>
        <Text
          style={{
            color: isDarkMode ? "var(--text-1)" : "#545D69",
            fontSize: "12.5px",
            fontWeight: "600",
            marginStart: "0.2rem",
          }}
        >
          {matchDetails?.team_a_details.name}
          {" vs "}
          {matchDetails?.team_b_details.name}
        </Text>
        {showHomeBtn && (
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={() => history.push("/")}
            sx={{
              marginLeft: "auto",
              marginRight: "auto",
              textTransform: "none",
              borderRadius: "50px",
              fontSize: "0.75rem",
              fontWeight: 600,
              px: 2,
              py: 0.6,
              color: "#1976d2",
              borderColor: "#1976d2",
              "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" },
            }}
          >
            Back To Home
          </Button>
        )}

        <Text
          style={{
            color: "#545D69",
            fontSize: "12.5px",
            fontWeight: "400",
            marginLeft: "auto",
            alignItems: "center",
          }}
        >
          {analystName}
        </Text>
      </View>

      <View
        style={{
          flexDirection: "row",
          width: "100%",
          maxHeight: "54vh",
          justifyContent: "space-between",
          marginTop: "0.8rem",
          paddingHorizontal: "1rem",
        }}
      >
        <View
          style={{
            width: "49%",
            height: "100%",
            backgroundColor: "var(--surface-2)",
            overflow: "auto",
            boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.22)",
          }}
        >
          <FlatList
            style={{
              height: "96%",
              width: "100%",
              flex: 1,
              backgroundColor: "transparent",
            }}
            ListEmptyComponent={
              <View
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text style={{ color: isDarkMode ? "#fff" : "#111827" }}>
                  DATA UNAVAILABLE
                </Text>
              </View>
            }
            data={props.team_a_data}
            stickyHeaderIndices={[0]}
            ListHeaderComponent={ListHeader}
            renderItem={({ index, item }) => (
              <AnalysisPreviewCard
                updateResult={props.updateResult}
                changeUserMappingData={changeUserMappingData}
                details={item}
              />
            )}
            keyExtractor={(item) => `${item.match}-${item.team}-${item.user}-a`}
          />
        </View>

        <View
          style={{
            width: "49%",
            height: "100%",
            backgroundColor: "var(--surface-2)",
            overflow: "auto",
            boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.22)",
          }}
        >
          <FlatList
            style={{
              flex: 1,
              width: "100%",
              height: "100%",
              backgroundColor: "transparent",
            }}
            data={props.team_b_data}
            ListEmptyComponent={
              <View
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text style={{ color: isDarkMode ? "#fff" : "#111827" }}>
                  DATA UNAVAILABLE
                </Text>
              </View>
            }
            stickyHeaderIndices={[0]}
            ListHeaderComponent={ListHeader}
            renderItem={({ index, item }) => (
              <AnalysisPreviewCard
                updateResult={props.updateResult}
                changeUserMappingData={changeUserMappingData}
                details={item}
              />
            )}
            keyExtractor={(item) => `${item.match}-${item.team}-${item.user}-b`}
          />
        </View>
      </View>

      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          paddingHorizontal: "1rem",
          marginTop: "1rem",
        }}
      >
        <View style={{ width: "49%", boxShadow: isDarkMode ? "0px 2px 6px rgba(0,0,0,0.5)" : "0px 2px 6px #00000020", backgroundColor: "var(--surface-1)" }}>
          <View
            style={{
              width: "100%",
              paddingHorizontal: "1rem",
            }}
          >
            <Stack
              sx={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "0.5rem",
              }}
            >
              <Text
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "var(--text-1)",
                }}
              >
                Match String
              </Text>
              <IconButton sx={{ p: 0, m: 0 }} onClick={() => setShowEdit(true)}>
                <EditNote sx={{ fontSize: "12px", color: "var(--text-1)" }} />
              </IconButton>
            </Stack>
            <TextInput
              style={{
                marginTop: "0.5rem",
                width: "100%",
                height: "24vh",
                fontSize: "10px",
                fontWeight: "400",
                color: "var(--text-2)",
              }}
              onChangeText={(text) => {
                props.SetFullAnalyseString({ id: matchId, string: text, source: "match-update" });
              }}
              value={props.matchString?.[matchId]}
              placeholder="Match String"
              multiline
            />
          </View>
          <Dialog fullScreen open={showEdit}>
            {/* Smaller Fixed AppBar */}
            <AppBar
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bgcolor: "#1976d2",
                zIndex: 1201,
              }}
            >
              <Toolbar
                disableGutters
                sx={{
                  minHeight: "36px !important", // force override
                  height: "36px",
                  px: 1.5, // horizontal padding
                  "& .MuiTypography-root": {
                    fontSize: "0.9rem",
                  },
                  "& .MuiButton-root": {
                    fontSize: "0.8rem",
                  },
                }}
              >
                {/* Not selectable, so the title can't be dragged into the string below. */}
                <Typography variant="subtitle1" sx={{ flex: 1, userSelect: "none" }}>
                  Edit Match String
                </Typography>
                <Button autoFocus color="inherit" onClick={onSaveStringPress}>
                  Save
                </Button>
              </Toolbar>
            </AppBar>

            {/* Scrollable content with adjusted padding */}
            <Box
              sx={{
                mt: "20px",
                pt: 7, // Adjust top padding to match the new smaller AppBar height
                p: 3,
                overflowY: "auto",
                height: "90vh",
                boxSizing: "border-box",
              }}
            >
              <TextField
                fullWidth
                multiline
                minRows={10}
                value={props.matchString?.[matchId]}
                onChange={(e) =>
                  props.SetFullAnalyseString({
                    id: matchId,
                    string: e.target.value,
                    source: "match-update",
                  })
                }
                placeholder="Match String"
                variant="outlined"
                sx={{
                  fontSize: "14px",
                  color: "rgba(57, 68, 82, 1)",
                  maxHeight: "80vh",
                }}
              />
            </Box>
          </Dialog>
        </View>
        <View style={{ width: "49%", gap: 10, marginStart: "1rem" }}>
          <View
            style={{
              width: "100%",
              flexDirection: "row",

              justifyContent: "space-between",
              marginStart: "1rem",
            }}
          >
            <View style={{ width: "30%" }}>
              <TouchableOpacity
                disabled={props.updatingMap}
                onPress={() => {
                  setShowSaveStatus(true);
                  const payload = {
                    organizer_id: matchDetails?.organizer_id,
                    data: userMappingRequest || [],
                  };
                  props.updateUserMatchMappings(
                    payload,
                    props.token,
                    onResponse
                  );
                }}
                style={{
                  backgroundColor: "rgba(240, 128, 34, 1)",
                  alignItems: "center",
                  paddingVertical: "0.8rem",
                  borderRadius: "3px",
                }}
              >
                <Text
                  style={{
                    marginLeft: "0.5rem",
                    color: "rgba(255, 255, 255, 1)",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  Save Lineup
                </Text>
              </TouchableOpacity>

              {props.updateResult?.failed_list?.length === 0 &&
                showSaveStatus && (
                  <Text
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "rgba(40, 125, 60, 1)",
                    }}
                  >
                    Lineup updated successfully.
                  </Text>
                )}
            </View>

            <View style={{ width: "65%" }}>
              <TouchableOpacity
                disabled={
                  props.updatingMap || isDisabled || props?.analysis_loading
                }
                onPress={() => {
                  if (!props.analysis_loading) {
                    handleOnRunAnalysisClick();
                  }
                }}
                style={{
                  borderWidth: 1,
                  borderColor: props?.analysis_error ? "red" : "transparent",
                  backgroundColor: "rgba(0, 146, 54, 1)",
                  alignItems: "center",
                  paddingVertical: "0.8rem",
                  borderRadius: "3px",
                  opacity: props?.analysis_loading ? 0.8 : isDisabled ? 0.5 : 1,
                }}
              >
                <Text
                  style={{
                    marginLeft: "0.5rem",
                    color: "rgba(255, 255, 255, 1)",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  {props?.analysis_loading ? (
                    <ActivityIndicator color="white" size="small" />
                  ) : isDisabled ? (
                    `Button disabled. Time left: ${formatTimeFromSeconds(
                      timeLeft
                    )}`
                  ) : (
                    "Run Analysis"
                  )}
                </Text>
              </TouchableOpacity>

              {props.analysis_error && showRunStatus && (
                <View style={{ flexDirection: "row" }}>
                  <Text
                    style={{
                      color: "tomato",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    ERROR!
                  </Text>
                  <Text
                    style={{
                      color: isDarkMode ? "var(--text-1)" : "#131313",
                      fontSize: "14px",
                      fontWeight: "600",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {props.analysis_error}
                  </Text>
                </View>
              )}
              {props.analysis_error && props.analysis_can_override && showRunStatus && (
                <TouchableOpacity
                  disabled={props?.analysis_loading}
                  onPress={handleOverrideRunClick}
                  style={{
                    borderWidth: 1,
                    borderColor: "tomato",
                    alignItems: "center",
                    paddingVertical: "0.6rem",
                    borderRadius: "3px",
                    marginTop: "0.5rem",
                    opacity: props?.analysis_loading ? 0.5 : 1,
                  }}
                >
                  <Text style={{ color: "tomato", fontSize: "12px", fontWeight: "600" }}>
                    Override validation and run
                  </Text>
                </TouchableOpacity>
              )}
              {props.analysis_result && showRunStatus && (
                <View style={{ flexDirection: "row" }}>
                  <Text
                    style={{
                      color: "green",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    SUCCESS
                  </Text>
                  {/* <Text style={{ color: "#131313" }}>{props.analysis_error}</Text> */}
                </View>
              )}
            </View>
          </View>
          <TouchableOpacity
            disabled={props.updatingMap}
            onPress={onSaveStringPress}
            style={{
              marginStart: "1rem",
              width: "100px",
              borderColor: "rgba(240, 128, 34, 1)",
              borderWidth: 1,
              backgroundColor: props.updatingMap
                ? isDarkMode
                  ? "#1F2937"
                  : "#F3F4F6"
                : "rgba(240, 128, 34, 1)",
              opacity: props.updatingMap ? 0.65 : 1,
              alignItems: "center",
              paddingVertical: "0.8rem",
              borderRadius: "3px",
            }}
          >
            <Text
              style={{
                marginLeft: "0.5rem",
                color: props.updatingMap
                  ? isDarkMode
                    ? "#E5E7EB"
                    : "#374151"
                  : "#fff",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              Save String
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};
const styles = {
  container: {
    flex: 1,
    height: "100%",
    width: "100%",
    overflow: "auto",
    paddingBottom: "1rem",
  },
  textStyle: {
    flex: 1,
    fontFamily: "Roboto",
    fontWeight: "700",
    fontSize: 10,
    color: "var(--text-1)",
    textAlign: "center",
  },
  headerTextStyle: {
    flex: 1,
    fontWeight: "600",
    fontSize: 10,
    color: "var(--text-1)",
    textAlign: "center",
  },
};

const ListHeader = () => {
  return (
    <View
      style={{
        flex: 1,
        width: "100%",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "var(--surface-1)",
        paddingVertical: "0.5rem",
        zIndex: 10,
        borderRadius: "0.2rem",
        gap: 4,
      }}
    >
      <Text style={styles.headerTextStyle}>ID</Text>
      <Text style={styles.headerTextStyle}>JR No.</Text>
      <Text style={styles.headerTextStyle}>Player Name</Text>
      <Text style={styles.headerTextStyle}>LineUp</Text>
      <Text style={styles.headerTextStyle}>Position</Text>
      <Text style={styles.headerTextStyle}>Total GT</Text>
      <Text style={styles.headerTextStyle}>Starting GT</Text>
      <Text style={styles.headerTextStyle}>Ending GT</Text>
    </View>
  );
};

const AnalysisPreviewCard = (props) => {
  const [updateStatus, setUpdateStatus] = useState("");

  const getAliasById = (id) => {
    const position = position_list.find((pos) => pos.id === id);
    return position ? position.alias : null;
  };
  const {
    user,
    jersey_number,
    user_name,
    match,
    team,
    game_time,
    preferred_position,
    start_gametime,
    end_gametime,
    lineup_notation,
  } = props.details;

  const failedList = props.updateResult?.failed_list;
  const successList = props.updateResult?.success_list;

  const setLineUpNotation = (notation) => {
    let lineupNotation = null;
    if (notation === 0) {
      lineupNotation = "NA";
    } else if (notation === 1) {
      lineupNotation = "Playing 11";
    } else {
      lineupNotation = "Substitute";
    }

    return lineupNotation;
  };

  const [time, setTime] = useState({
    totalGt: game_time,
    startGt: start_gametime,
    endGt: end_gametime,
  });

  useEffect(() => {
    setTime({
      totalGt: game_time,
      startGt: start_gametime,
      endGt: end_gametime,
    });
  }, [game_time, start_gametime, end_gametime]);

  useEffect(() => {
    if (failedList?.length > 0) {
      const check = failedList.find(
        (i) => i.user_id === user && i.team_id === team && i.match_id === match
      );
      if (check) {
        setUpdateStatus("FAILED");
        setTimeout(() => {
          setUpdateStatus("");
        }, 2000);
      } else {
        setUpdateStatus("UPDATED");
        setTimeout(() => {
          setUpdateStatus("");
        }, 2000);
      }
    }
  }, [failedList]);
  useEffect(() => {
    if (successList?.length > 0) {
      const check = successList.find(
        (i) => i.user_id === user && i.team_id === team && i.match_id === match
      );
      if (check) {
        setUpdateStatus("SUCCESS");
        setTimeout(() => {
          setUpdateStatus("");
        }, 2000);
      }
    }
  }, [successList]);

  return (
    <View
      style={{
        width: "100%",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        flex: 1,
        backgroundColor: "var(--surface-2)",
        marginVertical: "0.2rem",
        paddingVertical: "0.6rem",
        borderRadius: "0.2rem",
      }}
    >
      <Text
        style={{
          position: "absolute",
          color: updateStatus === "FAILED" ? "tomato" : "lime",
          fontSize: 10,
        }}
      >
        {updateStatus}
      </Text>

      <Text
        style={{
          flex: 0.12,
          textAlign: "center",
          fontSize: "10px",
          fontWeight: "600",
          color: "var(--text-1)",
        }}
      >
        {user}
      </Text>

      <View
        style={{
          flex: 0.12,
          paddingVertical: "0.4rem",
          backgroundColor: "rgba(25, 118, 210, 1)",
          borderRadius: "0.2rem",
        }}
      >
        <Text
          style={{
            textAlign: "center",
            color: "rgba(255, 255, 255, 1)",
            fontSize: "12px",
            fontWeight: "700",
          }}
        >
          {jersey_number}
        </Text>
      </View>
      <Text
        style={{
          maxWidth: "3rem",
          flex: 0.12,
          textAlign: "center",
          fontSize: "10px",
          fontWeight: "600",
          color: "var(--text-1)",
        }}
      >
        {user_name}
      </Text>
      <Text
        style={{
          flex: 0.12,
          textAlign: "center",
          fontSize: "10px",
          fontWeight: "600",
          color: "var(--text-1)",
        }}
      >
        {/* {lineup_notation === 1 ? "Playing 11" : "Substitute"} */}
        {setLineUpNotation(lineup_notation)}
      </Text>

      <View
        style={{
          flex: 0.12,
          borderColor: "rgba(40, 125, 60, 1)",
          borderRadius: "2rem",
          borderWidth: 1,
          paddingVertical: "0.4rem",
        }}
      >
        <Text
          style={{
            flex: 0.12,
            textAlign: "center",
            fontSize: "10px",
            fontWeight: "700",
            color: "rgba(40, 125, 60, 1)",
          }}
        >
          {getAliasById(preferred_position)}
        </Text>
      </View>

      <View style={{ alignItems: "center", flexDirection: "row" }}>
        <TextInput
          style={{
            width: "1.5rem",
            flex: 0.12,
            textAlign: "center",
            fontWeight: "700",
            fontSize: "10px",
            color: "var(--text-1)",
            paddingVertical: "0.4rem",
          }}
          defaultValue={time.totalGt}
          value={time.totalGt}
          onChangeText={(gameTime) => {
            setTime({
              startGt: parseInt(0),
              endGt: gameTime,
              totalGt: gameTime,
            });
            props.changeUserMappingData(
              team,
              match,
              user,
              gameTime,
              preferred_position,
              parseInt(0),
              gameTime
            );
          }}
          placeholder="NA"
        />
        <FaPencilAlt size={6} />
      </View>

      <View style={{ alignItems: "center", flexDirection: "row" }}>
        <TextInput
          style={{
            width: "1.5rem",
            flex: 0.12,
            textAlign: "center",
            fontWeight: "700",
            fontSize: "10px",
            color: "var(--text-1)",
            paddingVertical: "0.4rem",
          }}
          defaultValue={time.startGt}
          value={time.startGt}
          onChangeText={(startGameTime) => {
            const gameTime = time.endGt - startGameTime;
            setTime({
              ...time,
              totalGt: gameTime,
              startGt: startGameTime,
            });
            props.changeUserMappingData(
              team,
              match,
              user,
              gameTime,
              preferred_position,
              startGameTime,
              time.endGt
            );
          }}
          placeholder="NA"
        />
        <FaPencilAlt size={6} />
      </View>

      <View style={{ alignItems: "center", flexDirection: "row" }}>
        <TextInput
          style={{
            width: "1.5rem",
            flex: 0.12,
            textAlign: "center",
            fontWeight: "700",
            fontSize: "10px",
            color: "var(--text-1)",
            paddingVertical: "0.4rem",
          }}
          defaultValue={time.endGt}
          value={time.endGt}
          onChangeText={(endGameTime) => {
            const gameTime = endGameTime - time.startGt;
            setTime({
              ...time,
              totalGt: gameTime,
              endGt: endGameTime,
            });
            props.changeUserMappingData(
              team,
              match,
              user,
              gameTime,
              preferred_position,
              time.startGt,
              endGameTime
            );
          }}
          placeholder="NA"
        />
        <FaPencilAlt size={6} />
      </View>
    </View>
  );
};

const mapStateToProps = (state) => {
  return {
    isAuthenticated: state.auth.isAuthenticated,
    login_error: state.auth.login_error,
    loading: state.auth.loading,
    team_a_data: state.userMatchMapping.team_a_mapping,
    team_b_data: state.userMatchMapping.team_b_mapping,
    team_a_mapping_error: state.userMatchMapping.team_a_mapping_error,
    team_b_mapping_error: state.userMatchMapping.team_b_mapping_error,
    matchString: state.control.analysedString,
    analyst_id: state.auth.analyst_id,
    analysis_result: state.analysis.analysis_result,
    analysis_loading: state.analysis.analysis_loading,
    analysis_error: state.analysis.analysis_error,
    analysis_can_override: state.analysis.analysis_can_override,
    all_matches: state.match.all_matches,
    token: state.auth.token,
    updatingMap: state.userMatchMapping.updating,
    updateResult: state.userMatchMapping.update_result,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    ClearAnalysisError,
    SetFullAnalyseString: (str) => {
      dispatch(SetFullAnalyseString(str));
    },
    updateUserMatchMappings: (userList, token, callback) => {
      dispatch(UpdateUserMatchMappings(userList, token, callback));
    },
    analyzeMatch: (matchId, matchString, token, overrideReason = null) => {
      dispatch(AnalyzeMatch(matchId, matchString, token, overrideReason));
    },
    changeGameTime: (teamId, matchId, userId, gameTime, token) => {
      dispatch(ChangeGameTime(teamId, matchId, userId, gameTime, token));
    },
    getPlayerMatchMapping: (a, b, c) => {
      dispatch(getPlayerMatchMapping(a, b, c));
    },
    PushMatchStringToServer: (a, b, c, d) => {
      dispatch(PushMatchStringToServer(a, b, c, d));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MatchUpdate);
